import { CanceledSummary } from "@/queries/report";
import { components } from "@/lib/openapi";

export type ReportType = components["schemas"]["ReportType"];

export type DisruptionScope = {
  gateNames?: string[];
  terminalNames?: string[];
};

export function isLiveDisruptionContext(reportType?: ReportType | null): boolean {
  return reportType === "live" || reportType === "future";
}

export function isHistoricalReportDate(
  targetDate: string | null | undefined,
  referenceDate: Date = new Date()
): boolean {
  if (!targetDate) {
    return false;
  }
  const target = new Date(`${targetDate}T00:00:00`);
  const today = new Date(
    referenceDate.getFullYear(),
    referenceDate.getMonth(),
    referenceDate.getDate()
  );
  return target < today;
}

export function sumBreakdownForTerminals(
  summary: CanceledSummary | null | undefined,
  terminals: string[]
): { total: number; impacted: number } {
  if (!summary) {
    return { total: 0, impacted: 0 };
  }

  if (!summary.breakdown?.length || terminals.length === 0) {
    return {
      total: summary.total,
      impacted: summary.impacted_passengers_total,
    };
  }

  const terminalSet = new Set(terminals);
  const rows = summary.breakdown.filter((row) => terminalSet.has(row.name));
  if (rows.length === 0) {
    return { total: 0, impacted: 0 };
  }

  return rows.reduce(
    (acc, row) => ({
      total: acc.total + row.total,
      impacted: acc.impacted + row.impacted_passengers_total,
    }),
    { total: 0, impacted: 0 }
  );
}

export function scopedCanceledSummary(
  summary: CanceledSummary | null | undefined,
  terminals: string[]
): CanceledSummary {
  const empty: CanceledSummary = {
    total: 0,
    arrivals: 0,
    departures: 0,
    impacted_arrival_passengers: 0,
    impacted_departure_passengers: 0,
    impacted_passengers_total: 0,
    breakdown: summary?.breakdown ?? null,
  };

  if (!summary) {
    return empty;
  }

  if (!summary.breakdown?.length || terminals.length === 0) {
    return summary;
  }

  const terminalSet = new Set(terminals);
  const rows = summary.breakdown.filter((row) => terminalSet.has(row.name));
  const total = rows.reduce((sum, row) => sum + row.total, 0);
  const impacted = rows.reduce(
    (sum, row) => sum + row.impacted_passengers_total,
    0
  );

  return {
    ...summary,
    total,
    arrivals: 0,
    departures: 0,
    impacted_arrival_passengers: 0,
    impacted_departure_passengers: 0,
    impacted_passengers_total: impacted,
    breakdown: rows,
  };
}
